import { useState, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RotateCcw, Shuffle, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";

export const Flashcards = ({ terms = [] }) => {
  const [order, setOrder] = useState(() => terms.map((_, i) => i));
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  // Lock during the 500ms flip so spam-clicks don't chain animations.
  const flipLockRef = useRef(false);

  const cards = useMemo(() => order.map((i) => terms[i]).filter(Boolean), [order, terms]);

  if (cards.length === 0) {
    return <p className="text-sm text-muted-foreground py-8 text-center">No terms to study.</p>;
  }

  const card = cards[idx];

  const flip = () => {
    if (flipLockRef.current) return;
    flipLockRef.current = true;
    setFlipped((f) => !f);
    setTimeout(() => { flipLockRef.current = false; }, 520);
  };

  const go = (dir) => {
    setFlipped(false);
    setTimeout(() => {
      setIdx((i) => {
        const n = i + dir;
        if (n < 0) return cards.length - 1;
        if (n >= cards.length) return 0;
        return n;
      });
    }, 120);
  };

  const shuffle = () => {
    const shuffled = [...order].sort(() => Math.random() - 0.5);
    setFlipped(false);
    setOrder(shuffled);
    setIdx(0);
  };

  const reset = () => {
    setOrder(terms.map((_, i) => i));
    setIdx(0);
    setFlipped(false);
  };

  return (
    <div className="no-print">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-muted-foreground font-mono">
          Card {idx + 1} / {cards.length}
        </span>
        <div className="flex gap-2">
          <button
            data-testid="flashcard-shuffle"
            onClick={shuffle}
            className="flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:border-primary/50 transition"
          >
            <Shuffle className="h-3.5 w-3.5" /> Shuffle
          </button>
          <button
            onClick={reset}
            className="flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:border-primary/50 transition"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Reset
          </button>
        </div>
      </div>

      <div className="h-2 rounded-full bg-secondary overflow-hidden mb-5">
        <motion.div
          className="h-full bg-accent rounded-full"
          animate={{ width: `${((idx + 1) / cards.length) * 100}%` }}
        />
      </div>

      <div
        data-testid="flashcard"
        onClick={flip}
        className="relative cursor-pointer select-none"
        style={{ perspective: "1200px" }}
      >
        <motion.div
          className="relative w-full h-64 sm:h-72"
          animate={{ rotateY: flipped ? 180 : 0 }}
          transition={{ duration: 0.5 }}
          style={{ transformStyle: "preserve-3d" }}
        >
          {/* Front - term */}
          <div
            className="absolute inset-0 rounded-3xl border border-border bg-card shadow-sm flex flex-col items-center justify-center p-8 text-center"
            style={{ backfaceVisibility: "hidden" }}
          >
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-3">
              Term
            </span>
            <h3 className="font-serif text-3xl sm:text-4xl font-semibold">{card.term}</h3>
            <span className="mt-6 flex items-center gap-1.5 text-xs text-accent font-medium">
              <RotateCcw className="h-3.5 w-3.5" /> Tap to reveal definition
            </span>
          </div>
          {/* Back - definition */}
          <div
            className="absolute inset-0 rounded-3xl border border-primary/40 bg-primary text-primary-foreground shadow-sm flex flex-col items-center justify-center p-8 text-center"
            style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
          >
            <span className="text-xs font-semibold uppercase tracking-[0.2em] opacity-70 mb-3">
              Definition
            </span>
            <p className="text-base sm:text-lg leading-relaxed">{card.definition}</p>
          </div>
        </motion.div>
      </div>

      <div className="mt-5 flex items-center justify-center gap-4">
        <button
          data-testid="flashcard-prev"
          onClick={() => go(-1)}
          className="grid place-items-center h-11 w-11 rounded-full border border-border hover:border-primary/50 transition"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          data-testid="flashcard-flip"
          onClick={flip}
          className="rounded-full bg-accent text-accent-foreground px-6 py-2.5 text-sm font-semibold hover:opacity-90 transition active:scale-95"
        >
          Flip Card
        </button>
        <button
          data-testid="flashcard-next"
          onClick={() => go(1)}
          className="grid place-items-center h-11 w-11 rounded-full border border-border hover:border-primary/50 transition"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};
